<template>
  <div class="home-page">
    <div class="carousel-wrapper">
      <CarouselBanner :banners="carouselBanners" />
    </div>

    <!-- 未成年人保护模式入口条 -->
    <div class="teenager-entry-banner" @click="goYouthMode">
      <div class="teenager-entry-inner">
        <div class="teenager-entry-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </div>
        <div class="teenager-entry-info">
          <h3>未成年人保护模式</h3>
          <p v-if="teenagerEnabled">已开启 · 正在守护健康阅读，点击查看设置</p>
          <p v-else>为未成年人提供健康、安全的阅读环境</p>
        </div>
        <div class="teenager-entry-btn" :class="{ 'is-enabled': teenagerEnabled }">
          <template v-if="teenagerEnabled">
            <svg class="entry-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 12l2 2 4-4"/>
            </svg>
            已开启
          </template>
          <template v-else>一键开启</template>
        </div>
      </div>
    </div>

    <!-- 未成年人保护模式弹窗 -->
    <div v-if="showTeenagerModal" class="modal-overlay teenager-modal" @click="closeTeenagerModal">
      <div class="modal-content teenager-modal-content" @click.stop>
        <div class="teenager-modal-header">
          <svg class="teenager-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <h3>未成年人保护模式</h3>
        </div>
        <div class="teenager-modal-body">
          <p class="teenager-modal-desc">开启后将限制使用时长和访问时段</p>
          <ul class="teenager-modal-features">
            <li>每日使用时长限制</li>
            <li>夜间宵禁时段限制</li>
            <li>过滤不适宜内容，禁止购买消费</li>
          </ul>
        </div>
        <div class="teenager-modal-footer">
          <button class="teenager-cancel-btn" @click="closeTeenagerModal">暂不开启</button>
          <button class="teenager-confirm-btn" @click="enableTeenagerMode">一键开启</button>
        </div>
      </div>
    </div>

    <div class="section-list">
      <!-- 热播 / 人气 / 完结 标签页 -->
      <section class="comic-section tabs-section">
        <div class="tabs-header">
          <div class="tabs-title-group">
            <h2>精选漫画</h2>
            <p>发现最受欢迎的优质作品</p>
          </div>
          <div class="tabs-nav">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="tab-btn"
              :class="{ active: activeTab === tab.key }"
              @click="setTab(tab.key)"
            >
              <svg v-if="tab.icon" class="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="tab.icon"></svg>
              {{ tab.label }}
            </button>
          </div>
          <router-link to="/category" class="view-more">
            查看更多
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </router-link>
        </div>
        <div class="tabs-content">
          <div class="works-list" :key="activeTab">
            <div class="work-card" v-for="(item, index) in currentWorks" :key="item.id" @click="goDetail(item.id)" :style="{ animationDelay: `${index * 40}ms` }">
              <div class="cover-wrap">
                <img :src="item.cover" class="work-cover" />
                <div class="work-rank" :class="`rank-${index + 1}`" v-if="index < 3">{{ index + 1 }}</div>
                <span class="work-badge" v-if="item.isFree && !teenagerEnabled">免费</span>
                <span class="age-badge" v-if="teenagerEnabled">适龄内容</span>
              </div>
              <div class="work-info">
                <h3>{{ item.title }}</h3>
                <p>{{ item.author }}</p>
                <p class="desc">{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 人气榜 + 猜你喜欢 双栏 -->
      <div class="dual-layout">
        <section class="comic-section tabs-section">
          <div class="panel-header">
            <div class="panel-title">
              <div class="panel-bar"></div>
              <div>
                <h2>本周人气榜</h2>
                <p>按阅读量实时排序</p>
              </div>
            </div>
          </div>
          <div class="rank-list">
            <div class="rank-row" v-for="(item, index) in displayRankList" :key="item.id" @click="goDetail(item.id)">
              <span class="rank-no" :class="{ top: index < 3 }">{{ index + 1 }}</span>
              <img :src="item.cover" :alt="item.title" class="rank-cover" />
              <div class="rank-meta">
                <h4>{{ item.title }}</h4>
                <p>{{ item.author }}</p>
              </div>
              <span class="rank-heat">
                <svg viewBox="0 0 24 24"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>
                {{ formatNumber(item.views) }}
              </span>
            </div>
          </div>
        </section>

        <section class="comic-section tabs-section">
          <div class="panel-header">
            <div class="panel-title">
              <div class="panel-bar"></div>
              <div>
                <h2>猜你喜欢</h2>
                <p>根据全站热门随机推荐</p>
              </div>
            </div>
            <button class="refresh-btn" @click="refreshGuess">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              换一批
            </button>
          </div>
          <div class="guess-grid">
            <div class="guess-card" v-for="item in displayGuessWorks" :key="item.id" @click="goDetail(item.id)">
              <img :src="item.cover" :alt="item.title" class="guess-cover" />
              <div class="guess-meta">
                <h4>{{ item.title }}</h4>
                <p>{{ item.author }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- 继续阅读（有阅读记录且未开启青少年模式时显示） -->
      <section class="comic-section tabs-section history-section" v-if="historyWorks.length > 0 && !teenagerEnabled">
        <div class="panel-header">
          <div class="panel-title">
            <div class="panel-bar"></div>
            <div>
              <h2>继续阅读</h2>
              <p>接着上次的故事</p>
            </div>
          </div>
        </div>
        <div class="history-list">
          <div class="history-card" v-for="item in historyWorks" :key="item.comicId + '-' + item.chapterId" @click="goReader(item.chapterId, item.comicId)">
            <img :src="item.coverImage" :alt="item.title" class="history-cover" />
            <div class="history-meta">
              <h4>{{ item.title }}</h4>
              <p>读到：{{ item.chapterTitle }}</p>
              <span class="history-link">继续追更</span>
            </div>
            <span class="history-continue">
              继续
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </span>
          </div>
        </div>
      </section>

      <!-- 创作者招募横幅 -->
      <section class="creator-banner">
        <div class="creator-text">
          <h2>成为创作者，让作品被更多人看见</h2>
          <p>投稿你的原创漫画，或申请成为签约作者，获取平台流量扶持与收益分成</p>
        </div>
        <div class="creator-actions">
          <router-link to="/submission" class="creator-btn primary">立即投稿</router-link>
          <router-link to="/author-application" class="creator-btn ghost">作者认证</router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import CarouselBanner from '../components/Carousel.vue';
import { getHomeBanner, getComicList, getYouthConfig, BASE_URL, ReadingProgressManager } from '../api/index';
export default {
  name: 'HomePage',
  components: { CarouselBanner },
  data() {
    return {
      carouselBanners: [],
      works: {
        hot: [],
        popular: [],
        completed: []
      },
      activeTab: 'hot',
      // 全量作品缓存（首页各模块共用一次请求）
      allComics: [],
      rankList: [],
      guessWorks: [],
      historyWorks: [],
      tabs: [
        {
          key: 'hot',
          label: '热播',
          icon: '<path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/><path d="M12 6v6l4 2"/>'
        },
        {
          key: 'popular',
          label: '人气',
          icon: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'
        },
        {
          key: 'completed',
          label: '完结',
          icon: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'
        }
      ],
      teenagerEnabled: false,
      // 青少年模式内容白名单（库内共 10 部漫画，取 3 部治愈/萌宠向）
      youthWhitelist: [507, 511, 510],
      showTeenagerModal: false
    }
  },
  computed: {
    // 青少年模式白名单漫画：从全量缓存里取，保证各模块展示同一批
    youthComics() {
      return this.youthWhitelist
        .map(id => this.allComics.find(c => Number(c.id) === id))
        .filter(Boolean);
    },
    currentWorks() {
      if (this.teenagerEnabled && this.youthComics.length) return this.youthComics;
      return this.works[this.activeTab] || [];
    },
    displayRankList() {
      if (this.teenagerEnabled && this.youthComics.length) return this.youthComics;
      return this.rankList;
    },
    displayGuessWorks() {
      if (this.teenagerEnabled && this.youthComics.length) return this.youthComics;
      return this.guessWorks;
    }
  },
  created() {
    this.fetchBanner();
    this.fetchAllComics();
    this.loadHistory();
    this.checkTeenagerStatus();
  },
  methods: {
    // 数字格式化：12345 -> 1.2万
    formatNumber(num) {
      const n = Number(num) || 0;
      if (n >= 10000) return (n / 10000).toFixed(1) + '万';
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
      return String(n);
    },
    // 跳转阅读器（继续阅读）
    goReader(chapterId, comicId) {
      if (chapterId) {
        this.$router.push({ name: 'ComicReader', params: { id: chapterId, comicId: comicId } });
      }
    },
    // 读取本地阅读历史，取最近4条
    loadHistory() {
      try {
        const history = ReadingProgressManager.getHistory();
        this.historyWorks = history.slice(0, 4);
      } catch (e) {
        this.historyWorks = [];
      }
    },
    // 统一映射漫画数据
    mapComic(item) {
      return {
        id: item.id,
        cover: item.cover && item.cover.startsWith('http') ? item.cover : BASE_URL + (item.cover || ''),
        title: item.title,
        author: item.author,
        desc: item.description,
        isFree: Number(item.is_free) === 1,
        views: item.views || 0,
        chapters: item.chapter_count || 0,
        category: item.category_name || '漫画'
      };
    },
    // 随机打乱数组的工具函数
    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    // 一次请求全量作品，驱动所有首页模块
    async fetchAllComics() {
      try {
        const res = await getComicList({ page: 1, limit: 100 });
        if (res.data && res.data.code === 1) {
          const list = (res.data.data.list || []).map(this.mapComic);
          this.allComics = list;

          // 精选三个标签页（打乱顺序做差异化）
          this.works.hot = this.shuffleArray(list).slice(0, 10);
          this.works.popular = this.shuffleArray(list).slice(0, 10);
          this.works.completed = this.shuffleArray(list).slice(0, 10);

          // 人气榜：按阅读量排序取前8
          this.rankList = [...list].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);

          // 猜你喜欢：随机6部
          this.guessWorks = this.shuffleArray(list).slice(0, 6);
        }
      } catch (e) { /* ignore */ }
    },
    // 换一批猜你喜欢
    refreshGuess() {
      if (this.allComics.length) {
        this.guessWorks = this.shuffleArray(this.allComics).slice(0, 6);
      }
    },
    async checkTeenagerStatus() {
      const token = localStorage.getItem('userToken');
      if (!token) return;
      try {
        const res = await getYouthConfig();
        if (res.data && res.data.code === 1) {
          this.teenagerEnabled = res.data.data.enabled;
          if (!this.teenagerEnabled) {
            this.showTeenagerModal = true;
          }
        }
      } catch (e) {
        console.warn('获取青少年模式状态失败', e);
      }
    },
    closeTeenagerModal() {
      this.showTeenagerModal = false;
    },
    enableTeenagerMode() {
      this.showTeenagerModal = false;
      this.$router.push('/youth-mode');
    },
    goYouthMode() {
      this.$router.push('/youth-mode');
    },
    setTab(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
    },
    async fetchBanner() {
      try {
        const res = await getHomeBanner();
        if (res.data && res.data.code === 1) {
          this.carouselBanners = res.data.data.map(item => ({
            id: item.id,
            title: item.title,
            image: item.image_url,
            url: item.url,
            type: item.type,
            type_id: item.type_id
          }));
        }
      } catch (e) { /* ignore */ }
    },
    goDetail(id) {
      this.$router.push({ name: 'ComicDetail', params: { id } });
    },
  }
}
</script>

<style scoped>
.home-page {
  width: 100%;
  min-height: 100%;
  background: var(--bg);
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  position: relative;
}

.carousel-wrapper {
  width: 100vw;
  height: 500px;
  overflow: hidden;
  margin-bottom: 0;
  margin-left: calc(-50vw + 50%);
}
.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 未成年人保护模式入口条 */
.teenager-entry-banner {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background: linear-gradient(90deg, rgba(30, 60, 114, 0.95) 0%, rgba(42, 82, 152, 0.95) 100%);
  border-bottom: 1px solid rgba(96, 165, 250, 0.18);
  cursor: pointer;
  transition: background var(--t-fast);
}
.teenager-entry-banner:hover {
  background: linear-gradient(90deg, rgba(30, 60, 114, 1) 0%, rgba(42, 82, 152, 1) 100%);
}
.teenager-entry-inner {
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 1200px;
  min-height: 48px;
  margin: 0 auto;
  padding: 8px 24px;
}
.teenager-entry-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}
.teenager-entry-icon svg {
  width: 18px;
  height: 18px;
  display: block;
}
.teenager-entry-info {
  flex: 1;
  min-width: 0;
}
.teenager-entry-info h3 {
  margin: 0 0 3px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 600;
  color: #fff;
}
.teenager-entry-info p {
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.75);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.teenager-entry-btn {
  flex-shrink: 0;
  min-width: 76px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 14px;
  border: 1px solid var(--brand);
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: var(--brand);
  transition: background var(--t-fast), transform var(--t-fast);
}
.teenager-entry-banner:hover .teenager-entry-btn {
  background: var(--brand-hover);
}
.teenager-entry-btn.is-enabled {
  color: #fff;
  border-color: var(--success);
  background: var(--success);
}
.entry-check-icon {
  width: 13px;
  height: 13px;
}
@media (max-width: 768px) {
  .teenager-entry-inner {
    min-height: 44px;
    padding: 6px 16px;
    gap: 10px;
  }
  .teenager-entry-icon {
    width: 24px;
    height: 24px;
  }
  .teenager-entry-icon svg {
    width: 15px;
    height: 15px;
  }
  .teenager-entry-info h3 {
    font-size: 12px;
  }
  .teenager-entry-info p {
    font-size: 10px;
  }
  .teenager-entry-btn {
    min-width: 66px;
    height: 26px;
    padding: 0 10px;
    font-size: 10px;
  }
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 25, 41, 0.94);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border: 1px solid rgba(96, 165, 250, 0.25);
  border-radius: 12px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}
/* 未成年人保护模式弹窗 */
.teenager-modal {
  z-index: 1100;
  background: rgba(12, 25, 41, 0.94);
}
.teenager-modal-content {
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border-radius: 16px;
  width: 90%;
  max-width: 420px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(96, 165, 250, 0.25);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}
.teenager-modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px 18px;
  background: var(--brand);
  color: #fff;
}
.teenager-modal-icon {
  width: 32px;
  height: 32px;
  color: #fff;
  flex-shrink: 0;
}
.teenager-modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 1px;
}
.teenager-modal-body {
  padding: 24px 20px;
}
.teenager-modal-desc {
  font-size: 15px;
  color: var(--text);
  margin: 0 0 18px;
  text-align: center;
  font-weight: 500;
}
.teenager-modal-features {
  list-style: none;
  padding: 0;
  margin: 0;
}
.teenager-modal-features li {
  font-size: 14px;
  color: var(--text-dim);
  padding: 8px 0;
  padding-left: 26px;
  position: relative;
}
.teenager-modal-features li::before {
  content: '';
  position: absolute;
  left: 2px;
  top: 10px;
  width: 14px;
  height: 14px;
  background: var(--brand);
  border-radius: 50%;
}
.teenager-modal-features li::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 13px;
  width: 4px;
  height: 7px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(45deg);
}
.teenager-modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px 24px;
}
.teenager-cancel-btn,
.teenager-confirm-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--t-fast), transform var(--t-fast);
}
.teenager-cancel-btn {
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
}
.teenager-cancel-btn:hover {
  background: var(--surface);
}
.teenager-confirm-btn {
  background: var(--brand);
  color: #fff;
}
.teenager-confirm-btn:hover {
  transform: translateY(-2px);
  background: var(--brand-hover);
}

.section-list {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 30px 0 0;
}

/* ===== 双栏：人气榜 + 猜你喜欢 ===== */
.dual-layout {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 20px;
  align-items: stretch;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 28px 16px;
}
.panel-title {
  display: flex;
  align-items: center;
  gap: 14px;
}
.panel-bar {
  width: 4px;
  height: 38px;
  border-radius: 2px;
  background: linear-gradient(180deg, #4facfe 0%, #00f2fe 100%);
  flex-shrink: 0;
}
.panel-title h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0;
  color: var(--text);
}
.panel-title p {
  font-size: 12px;
  color: var(--text-faint);
  margin: 3px 0 0;
}

/* 人气榜列表 */
.rank-list {
  padding: 0 16px 18px;
}
.rank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background var(--t-fast);
}
.rank-row:hover {
  background: rgba(255, 255, 255, 0.07);
}
.rank-no {
  flex-shrink: 0;
  width: 22px;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  color: var(--text-faint);
  font-style: italic;
}
.rank-no.top {
  color: #fbbf24;
}
.rank-cover {
  flex-shrink: 0;
  width: 44px;
  height: 58px;
  object-fit: cover;
  border-radius: 6px;
}
.rank-meta {
  flex: 1;
  min-width: 0;
}
.rank-meta h4 {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-meta p {
  margin: 0;
  font-size: 12px;
  color: var(--text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-heat {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #fca5a5;
}
.rank-heat svg {
  width: 13px;
  height: 13px;
  fill: rgba(248, 113, 113, 0.25);
}

/* 猜你喜欢 */
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-dim);
  font-size: 12px;
  font-weight: 500;
  padding: 7px 14px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: all var(--t-fast);
  white-space: nowrap;
}
.refresh-btn:hover {
  color: #fff;
  border-color: var(--border-strong);
  background: rgba(255, 255, 255, 0.12);
}
.refresh-btn svg {
  width: 13px;
  height: 13px;
}
.guess-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 24px 24px;
}
.guess-card {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  cursor: pointer;
  transition: transform var(--t-fast), border-color var(--t-fast);
}
.guess-card:hover {
  transform: translateY(-4px);
  border-color: rgba(79, 172, 254, 0.35);
}
.guess-cover {
  width: 100%;
  height: 118px;
  object-fit: cover;
  display: block;
}
.guess-meta {
  padding: 9px 10px;
}
.guess-meta h4 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.guess-meta p {
  margin: 0;
  font-size: 11px;
  color: var(--text-faint);
}

/* ===== 继续阅读 ===== */
.history-section {
  padding-bottom: 8px;
}
.history-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  padding: 0 28px 24px;
}
.history-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px;
  cursor: pointer;
  transition: transform var(--t-fast), border-color var(--t-fast);
}
.history-card:hover {
  transform: translateY(-3px);
  border-color: rgba(79, 172, 254, 0.35);
}
.history-cover {
  flex-shrink: 0;
  width: 56px;
  height: 74px;
  object-fit: cover;
  border-radius: 8px;
}
.history-meta {
  flex: 1;
  min-width: 0;
}
.history-meta h4 {
  margin: 0 0 5px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-meta p {
  margin: 0 0 7px;
  font-size: 12px;
  color: var(--text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-link {
  font-size: 12px;
  color: #7dd3fc;
  text-decoration: none;
}
.history-link:hover {
  text-decoration: underline;
}
.history-continue {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  padding: 9px 16px;
  border-radius: var(--radius-pill);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
  transition: transform var(--t-fast), box-shadow var(--t-fast);
}
.history-card:hover .history-continue {
  transform: translateX(2px);
  box-shadow: 0 6px 16px rgba(79, 172, 254, 0.45);
}
.history-continue svg {
  width: 14px;
  height: 14px;
}

/* ===== 创作者招募横幅 ===== */
.creator-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 30px 34px;
  border-radius: var(--radius-lg);
  background: linear-gradient(120deg, rgba(79, 172, 254, 0.22) 0%, rgba(0, 242, 254, 0.12) 60%, rgba(255, 255, 255, 0.08) 100%);
  border: 1px solid rgba(79, 172, 254, 0.35);
  position: relative;
  overflow: hidden;
}
.creator-banner::before {
  content: '';
  position: absolute;
  right: -60px;
  top: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.25) 0%, transparent 70%);
  pointer-events: none;
}
.creator-text h2 {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
}
.creator-text p {
  margin: 0;
  font-size: 13px;
  color: var(--text-dim);
}
.creator-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
.creator-btn {
  display: inline-flex;
  align-items: center;
  padding: 11px 24px;
  border-radius: var(--radius-pill);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: transform var(--t-fast), box-shadow var(--t-fast), background var(--t-fast);
}
.creator-btn.primary {
  color: #fff;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  box-shadow: 0 4px 14px rgba(79, 172, 254, 0.35);
}
.creator-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(79, 172, 254, 0.5);
}
.creator-btn.ghost {
  color: var(--text);
  border: 1px solid var(--border-strong);
  background: rgba(255, 255, 255, 0.06);
}
.creator-btn.ghost:hover {
  background: rgba(255, 255, 255, 0.14);
}

/* 免费标签角标 */
.work-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #34D399 0%, #10b981 100%);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
}

/* 青少年模式：适龄内容胶囊角标 */
.rank-cover-wrap {
  position: relative;
  flex-shrink: 0;
}

.guess-cover-wrap {
  position: relative;
}

.age-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: rgba(16, 185, 129, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.38);
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
  letter-spacing: 0.2px;
  z-index: 2;
  pointer-events: none;
}

.age-badge.mini {
  top: 3px;
  right: 3px;
  padding: 1px 4px;
  font-size: 9px;
  border-radius: 6px;
  letter-spacing: 0;
}

/* 漫画内容区 */
.comic-section {
  background: var(--surface);
  border-radius: 16px;
  border: 1px solid var(--border);
  min-width: 200px;
  padding: 28px;
  position: relative;
}

/* Tabs 标签页头部 */
.tabs-section {
  padding: 0;
  overflow: hidden;
}

.tabs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 28px;
  border-bottom: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
}

.tabs-title-group h2 {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: var(--text);
}

.tabs-title-group p {
  font-size: 13px;
  color: var(--text-dim);
  margin: 0;
}

.tabs-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.18);
  padding: 5px;
  border-radius: 99px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border: none;
  border-radius: 99px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--t-fast);
  white-space: nowrap;
}

.tab-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}

.tab-btn.active {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(79, 172, 254, 0.35);
  font-weight: 600;
}

.tab-icon {
  width: 16px;
  height: 16px;
}

.view-more {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text-dim);
  font-size: 13px;
  text-decoration: none;
  transition: color var(--t-fast);
  white-space: nowrap;
}

.view-more svg {
  width: 15px;
  height: 15px;
  transition: transform var(--t-fast);
}

.view-more:hover {
  color: var(--brand);
}

.view-more:hover svg {
  transform: translateX(3px);
}

.tabs-content {
  padding: 24px 28px 32px;
}

.works-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 18px;
  width: 100%;
}

.work-card {
  background: var(--surface-2);
  border-radius: 12px;
  border: 1px solid var(--border);
  padding: 8px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 0;
  width: 100%;
  opacity: 0;
  transform: translateY(10px);
  animation: cardEnter 0.35s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  transition: transform var(--t-fast), border-color var(--t-fast), box-shadow var(--t-fast);
}

@keyframes cardEnter {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.work-card:hover {
  transform: translateY(-5px);
  border-color: rgba(79, 172, 254, 0.35);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.cover-wrap {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}

.work-cover {
  width: 100%;
  height: 190px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.work-card:hover .work-cover {
  transform: scale(1.04);
}

.work-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.work-rank.rank-1 {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #fff;
}

.work-rank.rank-2 {
  background: linear-gradient(135deg, #e5e7eb 0%, #9ca3af 100%);
  color: #1f2937;
}

.work-rank.rank-3 {
  background: linear-gradient(135deg, #fdba74 0%, #d97706 100%);
  color: #fff;
}

.work-info {
  text-align: left;
  width: 100%;
  padding: 0 4px 4px;
}

.work-info h3 {
  font-size: 14px;
  margin: 0 0 5px 0;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.work-info p {
  font-size: 12px;
  margin: 0 0 3px 0;
  color: var(--text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.work-info .desc {
  color: var(--text-faint);
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  margin-top: 3px;
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .works-list {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1100px) {
  .works-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .dual-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .section-list {
    padding-top: 20px;
    gap: 28px;
  }

  .panel-header {
    padding: 16px 18px 12px;
  }
  .panel-title h2 {
    font-size: 16px;
  }

  .guess-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 0 16px 18px;
  }
  .guess-cover {
    height: 92px;
  }
  .guess-meta {
    padding: 7px 8px;
  }
  .guess-meta h4 {
    font-size: 12px;
  }
  .guess-meta p {
    font-size: 10px;
  }

  .rank-list {
    padding: 0 10px 12px;
  }
  .rank-row {
    padding: 8px 8px;
    gap: 10px;
  }
  .rank-cover {
    width: 38px;
    height: 50px;
  }
  .rank-heat {
    font-size: 11px;
  }

  .history-list {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 0 18px 18px;
  }
  .history-continue {
    padding: 8px 13px;
    font-size: 12px;
  }

  .creator-banner {
    flex-direction: column;
    align-items: flex-start;
    padding: 22px 20px;
    gap: 16px;
  }
  .creator-text h2 {
    font-size: 18px;
  }
  .creator-actions {
    width: 100%;
  }
  .creator-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 16px;
  }

  .tabs-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 20px;
  }

  .tabs-title-group h2 {
    font-size: 18px;
  }

  .tabs-nav {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .tabs-nav::-webkit-scrollbar {
    display: none;
  }

  .tab-btn {
    flex: 1;
    justify-content: center;
    padding: 8px 14px;
    font-size: 13px;
  }

  .view-more {
    display: none;
  }

  .tabs-content {
    padding: 18px 20px 24px;
  }

  .comic-section {
    padding: 0;
    border-radius: 14px;
  }

  .works-list {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .work-cover {
    height: 160px;
  }

  .work-info h3 {
    font-size: 13px;
  }

  .work-info p {
    font-size: 11px;
  }

  .work-info .desc {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .works-list {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .work-cover {
    height: 130px;
  }

  .work-info h3 {
    font-size: 12px;
  }

  .work-info p {
    font-size: 10px;
  }

  .work-info .desc {
    font-size: 9px;
  }
}
</style> 